// Field layout for each alarm receiver on the camera's Alert Settings tab
// (ENG-2483). The tab renders one card per stored alert from these configs
// instead of a hand-written table per integration.
//
// Payload parity matters: the edit tab keeps a local copy of each alert built
// by `initLocal`, and that copy is spread into the PATCH body
// (updatedAlertStreams[updateKey][alertId]). Each `initLocal` reproduces the
// object the old per-integration code built, field for field, so the PATCH
// bodies are unchanged.

export type ReceiverKey =
  | "immix"
  | "dice"
  | "sureview"
  | "sentinel"
  | "bold"
  | "patriot"
  | "softguard"
  | "evalink"
  | "sysaid";

export type ReceiverFieldKind =
  | "text"
  | "secret"
  | "sureviewServer"
  // Shown, never editable here (Evalink's IDs are set by the Evalink flow).
  | "readOnly";

export interface ReceiverField {
  key: string;
  label: string;
  kind: ReceiverFieldKind;
}

/** How the "what is sent" choice is edited for this receiver. */
export type ReceiverDeliveryControl = "none" | "drawIgnoreZones" | "patriot";

export interface ReceiverConfig {
  key: ReceiverKey;
  /** Name shown to users. */
  name: string;
  /** Array on each camera stream holding these alerts. */
  alertsKey: string;
  fields: ReceiverField[];
  delivery: ReceiverDeliveryControl;
  /** Local edit copy of one stored alert (spread into the PATCH body). */
  initLocal: (alert: any) => Record<string, any>;
}

export const SUREVIEW_SERVER_OPTIONS: [string, string][] = [
  ["US SMTP Server", "us-smtp.sureviewops.com"],
  ["EU SMTP Server", "eu-smtp.sureviewops.com"],
];

/** "us-smtp.sureviewops.com" -> "US SMTP Server" (undefined when unknown). */
export const sureviewServerName = (value: unknown): string | undefined =>
  SUREVIEW_SERVER_OPTIONS.find(([, host]) => host === value)?.[0];

/** "US SMTP Server" -> "us-smtp.sureviewops.com" (undefined when unknown). */
export const sureviewServerHost = (name: unknown): string | undefined =>
  SUREVIEW_SERVER_OPTIONS.find(([label]) => label === name)?.[1];

const text = (key: string, label: string): ReceiverField => ({
  key,
  label,
  kind: "text",
});

/** Copy of `alert` with each key defaulted to "" (the old `x || ""`). */
const pickText = (alert: any, keys: string[]) =>
  keys.reduce<Record<string, any>>((out, key) => {
    out[key] = alert?.[key] || "";
    return out;
  }, {});

export const RECEIVER_CONFIGS: Record<ReceiverKey, ReceiverConfig> = {
  immix: {
    key: "immix",
    name: "Immix",
    alertsKey: "immix_alerts",
    fields: [
      text("immix_server", "Server"),
      text("immix_port", "Port"),
      text("immix_email", "Email"),
      text("immix_input1", "Input 1"),
    ],
    delivery: "drawIgnoreZones",
    initLocal: (alert) => ({
      ...pickText(alert, [
        "immix_server",
        "immix_port",
        "immix_email",
        "immix_input1",
      ]),
      draw_ignore_zones: alert.draw_ignore_zones || false,
    }),
  },
  dice: {
    key: "dice",
    name: "Dice",
    alertsKey: "dice_alerts",
    fields: [
      text("dice_server", "Server"),
      text("dice_port", "Port"),
      text("dice_email", "Email"),
      text("dice_input1", "Input 1"),
    ],
    delivery: "drawIgnoreZones",
    initLocal: (alert) => ({
      ...pickText(alert, [
        "dice_server",
        "dice_port",
        "dice_email",
        "dice_input1",
      ]),
      draw_ignore_zones: alert.draw_ignore_zones || false,
    }),
  },
  sureview: {
    key: "sureview",
    name: "SureView",
    alertsKey: "sureview_alerts",
    fields: [
      { key: "sureview_server", label: "Server", kind: "sureviewServer" },
      text("sureview_subject", "Subject"),
      text("sureview_cc", "CC"),
      text("sureview_email", "Email"),
    ],
    delivery: "none",
    initLocal: (alert) =>
      pickText(alert, [
        "sureview_server",
        "sureview_subject",
        "sureview_cc",
        "sureview_email",
      ]),
  },
  sentinel: {
    key: "sentinel",
    name: "Sentinel",
    alertsKey: "sentinel_alerts",
    fields: [
      text("sentinel_server", "Server"),
      text("sentinel_site_id", "Site ID"),
      text("sentinel_device_address", "Device address"),
    ],
    delivery: "drawIgnoreZones",
    initLocal: (alert) => ({
      ...pickText(alert, [
        "sentinel_server",
        "sentinel_site_id",
        "sentinel_device_address",
      ]),
      draw_ignore_zones: alert.draw_ignore_zones || false,
    }),
  },
  bold: {
    key: "bold",
    name: "Bold",
    alertsKey: "bold_alerts",
    fields: [
      text("bold_server", "Server"),
      text("bold_port", "Port"),
      text("bold_id", "ID"),
      text("bold_sdnis", "SDNIS"),
      text("bold_zone", "Zone"),
      text("bold_alarmtype", "Alarm type"),
    ],
    delivery: "none",
    initLocal: (alert) =>
      pickText(alert, [
        "bold_server",
        "bold_port",
        "bold_id",
        "bold_sdnis",
        "bold_zone",
        "bold_alarmtype",
      ]),
  },
  patriot: {
    key: "patriot",
    name: "Patriot",
    alertsKey: "patriot_alerts",
    fields: [
      text("patriot_server", "Server"),
      text("patriot_client_no", "Client number"),
      text("patriot_username", "Username"),
      { key: "patriot_password", label: "Password", kind: "secret" },
      text("patriot_zone_number", "Zone number"),
      text("patriot_type_number", "Type number"),
    ],
    delivery: "patriot",
    initLocal: (alert) => ({
      ...pickText(alert, [
        "patriot_server",
        "patriot_client_no",
        "patriot_username",
        "patriot_password",
        "patriot_zone_number",
      ]),
      patriot_send_video: alert.patriot_send_video || false,
      patriot_type_number: alert.patriot_type_number || "",
      draw_ignore_zones: alert.draw_ignore_zones || false,
    }),
  },
  softguard: {
    key: "softguard",
    name: "SoftGuard",
    alertsKey: "softguard_alerts",
    fields: [
      text("server", "Server"),
      text("port", "Port"),
      text("zone", "Zone"),
      text("account", "Account"),
      text("user", "User"),
      text("user_ext", "User extension"),
    ],
    delivery: "none",
    initLocal: (alert) =>
      pickText(alert, [
        "server",
        "port",
        "zone",
        "account",
        "user",
        "user_ext",
      ]),
  },
  evalink: {
    key: "evalink",
    name: "Evalink",
    alertsKey: "evalink_alerts",
    fields: [
      { key: "evalink_token", label: "Token", kind: "readOnly" },
      { key: "evalink_device_id", label: "Device ID", kind: "readOnly" },
      { key: "evalink_company_id", label: "Company ID", kind: "readOnly" },
      text("partition_id", "Partition ID"),
      text("alarm_zone_id", "Alarm zone ID"),
    ],
    delivery: "none",
    initLocal: (alert) => ({
      ...pickText(alert, [
        "evalink_token",
        "evalink_device_id",
        "evalink_company_id",
        "partition_id",
        "alarm_zone_id",
      ]),
      active: alert.active ?? true,
    }),
  },
  sysaid: {
    key: "sysaid",
    name: "SysAid",
    alertsKey: "sysaid_alerts",
    fields: [
      text("sysaid_server", "Server"),
      text("sysaid_client_local", "Client local"),
      text("sysaid_username", "Username"),
      { key: "sysaid_password", label: "Password", kind: "secret" },
    ],
    delivery: "none",
    initLocal: (alert) =>
      pickText(alert, [
        "sysaid_server",
        "sysaid_client_local",
        "sysaid_username",
        "sysaid_password",
      ]),
  },
};

/** Render order on the tab (webhooks sit after SureView, as before). */
export const RECEIVER_ORDER_BEFORE_WEBHOOKS: ReceiverKey[] = [
  "immix",
  "dice",
  "sureview",
];
export const RECEIVER_ORDER_AFTER_WEBHOOKS: ReceiverKey[] = [
  "sentinel",
  "bold",
  "patriot",
  "softguard",
  "evalink",
  "sysaid",
];

/** Stored booleans arrive as true / "True" / "true". */
export const isOn = (value: unknown): boolean =>
  value === true || value === "True" || value === "true";

/** A stream's stored alerts of one type (streams are loosely typed). */
export const streamAlerts = (
  stream: unknown,
  alertsKey: string
): any[] | undefined =>
  (stream as Record<string, any[] | undefined> | undefined)?.[alertsKey];
